import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Check, Loader2, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PricingPage() {
    const { user, refreshUser } = useAuth();
    const navigate = useNavigate();
    const [subInfo, setSubInfo] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const fetchSub = async () => {
            try {
                const { data } = await axios.get(`${API}/subscription/info`, { withCredentials: true });
                setSubInfo(data);
            } catch { /* silent */ }
        };
        fetchSub();
    }, []);

    const handleSubscribe = async () => {
        setLoading(true);
        try {
            const origin = window.location.origin;
            const { data } = await axios.post(`${API}/subscription/checkout`, { origin_url: origin }, { withCredentials: true });
            if (data.url) {
                window.location.href = data.url;
            } else {
                toast.error("Failed to create checkout session");
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || "Checkout error");
        }
        setLoading(false);
    };

    const status = subInfo?.subscription?.status || user?.subscription_status || "trial";
    const daysLeft = subInfo?.subscription?.days_left ?? 0;

    return (
        <div className="min-h-screen bg-[#0A0A0A]" data-testid="pricing-page">
            <Navbar />
            <div className="max-w-xl mx-auto px-6 py-16">
                <p className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-500 mb-3 text-center">Subscription</p>
                <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-white text-center mb-3" style={{ fontFamily: 'Chivo, sans-serif' }}>
                    TradeNalysis Pro
                </h1>
                <p className="text-sm text-zinc-400 text-center mb-12">Full access to AI-powered trading analysis</p>

                {/* Status Card */}
                <div className="border border-white/10 bg-[#121212] rounded-2xl p-6 mb-8">
                    <div className="flex items-center justify-between mb-4">
                        <p className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-500">Current Status</p>
                        <Badge variant="outline" className={`rounded-xl text-[10px] tracking-widest uppercase font-bold border ${status === 'active' ? 'text-[#00C805] border-[#00C805]/30' : status === 'trial' ? 'text-[#007AFF] border-[#007AFF]/30' : 'text-[#FF3B30] border-[#FF3B30]/30'}`} data-testid="current-status-badge">
                            {status}
                        </Badge>
                    </div>
                    <p className="text-sm text-zinc-300" data-testid="subscription-message">
                        {subInfo?.subscription?.message || `Status: ${status}`}
                    </p>
                    {(status === "trial" || status === "active") && daysLeft > 0 && (
                        <div className="mt-3 bg-white/5 h-1">
                            <div
                                className={`h-full ${status === 'active' ? 'bg-[#00C805]' : 'bg-[#007AFF]'}`}
                                style={{ width: `${Math.min((daysLeft / (status === 'active' ? 30 : 3)) * 100, 100)}%` }}
                            />
                        </div>
                    )}
                </div>

                {/* Plan Card */}
                <div className="border border-[#007AFF]/30 bg-[#121212] rounded-2xl p-8">
                    <div className="flex items-baseline gap-1 mb-2">
                        <span className="text-5xl font-black text-white" style={{ fontFamily: 'Chivo, sans-serif' }}>$19</span>
                        <span className="text-zinc-500 text-sm">/month</span>
                    </div>
                    <p className="text-sm text-zinc-400 mb-8">30 days of full access</p>

                    <ul className="space-y-3 mb-8">
                        {[
                            "AI-powered entries, stop losses & take profits",
                            "All instruments (Stocks, Forex, Crypto, Futures)",
                            "Real-time price charts & data",
                            "Unlimited AI analyses per month",
                            "Full analysis history",
                            "GPT-5.2 powered analysis engine",
                        ].map((item, i) => (
                            <li key={i} className="flex items-center gap-3 text-sm text-zinc-300">
                                <Check className="w-4 h-4 text-[#007AFF] flex-shrink-0" />
                                {item}
                            </li>
                        ))}
                    </ul>

                    {status === "active" ? (
                        <div className="text-center">
                            <p className="text-sm text-[#00C805] font-semibold mb-3">Your subscription is active</p>
                            <Button
                                onClick={() => navigate("/dashboard")}
                                className="w-full bg-[#007AFF] hover:bg-[#3395FF] text-white rounded-xl py-5 font-bold"
                                data-testid="go-dashboard-btn"
                            >
                                Go to Dashboard <ArrowRight className="w-4 h-4 ml-2" />
                            </Button>
                        </div>
                    ) : (
                        <Button
                            onClick={handleSubscribe}
                            disabled={loading}
                            className="w-full bg-[#007AFF] hover:bg-[#3395FF] text-white rounded-xl py-5 text-base font-bold"
                            data-testid="subscribe-btn"
                        >
                            {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...</> : <>Subscribe Now - $19/month</>}
                        </Button>
                    )}
                </div>

                <p className="text-xs text-zinc-600 text-center mt-6">
                    Secure payment via Stripe. Cancel anytime.
                </p>
            </div>
        </div>
    );
}
